// Informe Gerencial del coordinador (Fase A — Robotpack 2026-09).
//
// Pantalla de armado del PDF: período con el selector COMPARTIDO (lib/periodo,
// el mismo del Dashboard y de Análisis de paros, persistido en la URL como
// ?p=&ancla= / ?p=rango&desde=&hasta=), comparación opcional "a la misma
// altura" (DN8), checklist de indicadores y tarjetas de preview por sección. La
// descarga genera un PDF A4 apaisado con marca Jidokap (informeGerencial.ts).
// Bases declaradas (DN3): producción/cumplimiento/facturación validadas por
// supervisión; paros, Pits y personal = registro operativo cerrado.
//
// La comparación se resuelve con DOS llamadas por endpoint y merge en el FE
// (sin cambios de backend); las llamadas van con concurrencia acotada a 4.
// Una sección cuyo endpoint falla se marca "Sin datos" y no tumba el informe.

import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { CoordinadorSidebar } from '../../components/layout/CoordinadorSidebar';
import { RoleLayout } from '../../components/layout/RoleLayout';
import { useMe } from '../../lib/api/queries';
import { useAuth } from '../../lib/auth';
import { cn } from '../../lib/utils';
import { copM, miles } from '../../lib/informeCanvas';
import { BarraPeriodo, usePeriodo, type Preset } from '../../lib/periodo/periodo';
import { inicioSemanaOperativaDeKey } from 'schemas';
import { descargarInformeGerencial } from './informeGerencial';
import {
  cargarDatosInforme,
  etiquetaComparacion,
  periodoComparacion,
  SECCIONES_INFORME,
  type DatosInformeGerencial,
  type ModoComparacion,
  type Periodo,
  type SeccionInforme,
} from './informeGerencialDatos';
import { descargarInformeGerencialPptx } from './informeGerencialPptx';
import {
  cargarDatosGranulares,
  digestCostos,
  digestFacturacion,
  digestNivel,
  extraerRecursos,
  mesesDelRango,
} from './informeGerencialPptxDatos';

// ── Helpers de UI ────────────────────────────────────────────────────────────

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

function fmtFecha(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return `${d} ${MESES[(m ?? 1) - 1]} ${y}`;
}

function labelPeriodo(p: Periodo): string {
  return `${fmtFecha(p.desde)} – ${fmtFecha(p.hasta)}`;
}

/** Presets del informe: los del selector compartido que tienen sentido gerencial. */
const PRESETS_INFORME: Preset[] = ['semana', 'mes', 'trimestre', 'corte', 'rango'];

type EstadoSeccion = 'cargando' | 'lista' | 'sin_datos';

interface PreviewSeccion {
  id: SeccionInforme;
  estado: EstadoSeccion;
  headline: string;
}

/** Estado + número gordo de cada sección a partir de los datos cargados. */
function previews(
  seleccion: ReadonlySet<SeccionInforme>,
  datos: DatosInformeGerencial | null,
  cargando: boolean,
): PreviewSeccion[] {
  return SECCIONES_INFORME.filter((s) => seleccion.has(s.id)).map((s) => {
    if (cargando || !datos) return { id: s.id, estado: 'cargando', headline: '…' };
    switch (s.id) {
      case 'resumen': {
        const ok = datos.cumplimiento.ok || datos.paros.ok || datos.nivel.ok;
        const real = datos.cumplimiento.ok
          ? datos.cumplimiento.data.totales.cantidad_real_total
          : 0;
        return {
          id: s.id,
          estado: ok ? 'lista' : 'sin_datos',
          headline: ok ? `${miles(real)} und` : '—',
        };
      }
      case 'facturacion':
        return {
          id: s.id,
          estado: datos.facturacion.ok ? 'lista' : 'sin_datos',
          headline: datos.facturacion.ok ? copM(datos.facturacion.data.totales.valor_total) : '—',
        };
      case 'nivel':
        return {
          id: s.id,
          estado: datos.nivel.ok ? 'lista' : 'sin_datos',
          headline:
            datos.nivel.ok && datos.nivel.data.acumulado.cumplimiento_real_pct !== null
              ? `${Math.round(datos.nivel.data.acumulado.cumplimiento_real_pct)}% real`
              : '—',
        };
      case 'productividad': {
        const ok = datos.cumplimiento.ok;
        const pct = ok ? datos.cumplimiento.data.totales.cumplimiento_meta_promedio_pct : null;
        return {
          id: s.id,
          estado: ok ? 'lista' : 'sin_datos',
          headline: pct === null ? '—' : `${Math.round(pct)}% meta`,
        };
      }
      case 'lideres':
        return {
          id: s.id,
          estado: datos.personal.ok ? 'lista' : 'sin_datos',
          headline: datos.personal.ok
            ? `${datos.personal.data.lideres.filter((l) => l.bloques_capturados > 0).length} líderes`
            : '—',
        };
      case 'paros':
        return {
          id: s.id,
          estado: datos.paros.ok ? 'lista' : 'sin_datos',
          // B2 paro total registrado (incluye programados), solo cerrados.
          headline: datos.paros.ok
            ? `${datos.paros.data.totales.horas_totales.toLocaleString('es-CO', { maximumFractionDigits: 1 })} h total`
            : '—',
        };
      case 'pits': {
        const ok = datos.pits.ok && datos.pits.data.indicadores.length > 0;
        return {
          id: s.id,
          estado: ok ? 'lista' : 'sin_datos',
          headline: ok && datos.pits.ok ? `${datos.pits.data.indicadores.length} causal(es)` : '—',
        };
      }
      case 'costos': {
        const ok =
          datos.cumplimiento.ok && datos.cumplimiento.data.totales.valor_producido_total != null;
        return {
          id: s.id,
          estado: ok ? 'lista' : 'sin_datos',
          headline:
            ok && datos.cumplimiento.ok && datos.cumplimiento.data.totales.utilidad_total != null
              ? copM(datos.cumplimiento.data.totales.utilidad_total)
              : '—',
        };
      }
    }
  });
}

// ── Página ───────────────────────────────────────────────────────────────────

export function InformeGerencialPage() {
  const { state } = useAuth();
  const me = useMe();

  // Período COMPARTIDO (URL): el mismo contexto que trae el usuario del
  // Dashboard o de Análisis de paros. 'Mes anterior' se alcanza con ◀.
  const per = usePeriodo(undefined, 'mes', { url: true });
  const [modoComp, setModoComp] = useState<ModoComparacion>('ninguno');
  const [compCustom, setCompCustom] = useState<Periodo>({ desde: '', hasta: '' });
  const [seleccion, setSeleccion] = useState<Set<SeccionInforme>>(
    () => new Set(SECCIONES_INFORME.map((s) => s.id)),
  );

  const [datos, setDatos] = useState<DatosInformeGerencial | null>(null);
  const [cargando, setCargando] = useState(false);
  const [generando, setGenerando] = useState(false);
  // PowerPoint (estructura espejo del deck del cliente — no sigue el PDF).
  const [incluirRrhh, setIncluirRrhh] = useState(true);
  const [generandoPptx, setGenerandoPptx] = useState(false);
  const [progresoPptx, setProgresoPptx] = useState<string | null>(null);

  const periodo: Periodo | null = useMemo(
    () => ({ desde: per.periodo.desde, hasta: per.periodo.hasta }),
    [per.periodo.desde, per.periodo.hasta],
  );

  const comp: Periodo | null = useMemo(() => {
    if (!periodo) return null;
    return periodoComparacion(
      modoComp,
      periodo,
      compCustom.desde && compCustom.hasta && compCustom.desde <= compCustom.hasta
        ? compCustom
        : null,
      { desde: per.periodo.desdeAnt, hasta: per.periodo.hastaAnt },
    );
  }, [modoComp, periodo, compCustom, per.periodo.desdeAnt, per.periodo.hastaAnt]);

  // Etiquetas que viajan al PDF/PPTX: dicen si el período va parcial y a qué
  // altura se compara (DN8). Pantalla y documento comparten el mismo texto.
  const periodoLabel = periodo
    ? `${labelPeriodo(periodo)}${per.periodo.parcialLabel ? ` (${per.periodo.parcialLabel})` : ''}`
    : '';
  const compLabel =
    comp && modoComp !== 'ninguno'
      ? `${etiquetaComparacion(modoComp, per.periodo.isCurrent) ?? 'vs.'} · ${labelPeriodo(comp)}`
      : null;

  // Carga automática al cambiar período/comparación/selección. El token evita
  // que una respuesta vieja pise una nueva (carreras al cambiar filtros).
  useEffect(() => {
    if (!periodo || seleccion.size === 0) {
      setDatos(null);
      return;
    }
    let vigente = true;
    setCargando(true);
    cargarDatosInforme(periodo, comp, seleccion)
      .then((d) => {
        if (vigente) setDatos(d);
      })
      .finally(() => {
        if (vigente) setCargando(false);
      });
    return () => {
      vigente = false;
    };
    // seleccion se serializa para no depender de la identidad del Set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodo?.desde, periodo?.hasta, comp?.desde, comp?.hasta, [...seleccion].sort().join(',')]);

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  const lista = previews(seleccion, datos, cargando);
  const listasOk = lista.filter((p) => p.estado === 'lista').map((p) => p.id);
  const puedeDescargar = !cargando && !generando && periodo !== null && listasOk.length > 0;

  const toggle = (id: SeccionInforme) => {
    setSeleccion((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const descargar = async () => {
    if (!datos || !periodo) return;
    setGenerando(true);
    try {
      // Labels de contexto desde el primer endpoint que los trae.
      const conScope = [datos.cumplimiento, datos.paros, datos.personal, datos.pits].find(
        (r): r is Extract<typeof r, { ok: true }> => r.ok,
      );
      const plantasLabel = conScope
        ? conScope.data.scope.plantas_asignadas.map((p) => p.nombre).join(' · ')
        : 'Plantas asignadas';
      await descargarInformeGerencial({
        tenantNombre: me.data?.tenant?.nombre ?? conScope?.data.tenant.nombre ?? '',
        plantasLabel,
        periodoLabel,
        compLabel,
        descargadoPor: me.data?.user ? `${me.data.user.nombre} ${me.data.user.apellido}` : '—',
        secciones: listasOk,
        totalDisponibles: SECCIONES_INFORME.length,
        datos,
      });
      toast.success('Informe generado. Revise su carpeta de descargas.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'No pudimos generar el informe.');
    } finally {
      setGenerando(false);
    }
  };

  // PPTX espejo del deck del cliente: fetch granular LAZY (solo acá), con
  // progreso visible. Recarga la base COMPLETA (sin depender del checklist —
  // el deck es fijo) y luego el detalle por área/línea × mes.
  const descargarPptx = async () => {
    if (!periodo) return;
    setGenerandoPptx(true);
    setProgresoPptx('Cargando datos base…');
    try {
      const todas = new Set<SeccionInforme>(SECCIONES_INFORME.map((s) => s.id));
      const base = await cargarDatosInforme(periodo, null, todas);
      const recursos = extraerRecursos(base);
      if (recursos.lineas.length === 0) {
        toast.error('No pudimos resolver las áreas y líneas del tenant para armar el deck.');
        return;
      }
      const meses = mesesDelRango(periodo);
      const granular = await cargarDatosGranulares(recursos, meses, setProgresoPptx);

      const conScope = [base.cumplimiento, base.paros, base.personal, base.pits].find(
        (r): r is Extract<typeof r, { ok: true }> => r.ok,
      );
      await descargarInformeGerencialPptx(
        {
          tenantNombre: me.data?.tenant?.nombre ?? conScope?.data.tenant.nombre ?? '',
          plantaLabel: conScope
            ? conScope.data.scope.plantas_asignadas.map((p) => p.nombre).join(' · ')
            : 'Plantas asignadas',
          periodoLabel,
          generadoPor: me.data?.user ? `${me.data.user.nombre} ${me.data.user.apellido}` : '—',
          incluirRrhh,
          facturacion: digestFacturacion(base),
          nivel: digestNivel(base, (iso) => inicioSemanaOperativaDeKey(iso, 1)),
          productividad: granular.productividad,
          lideres: granular.lideres,
          paros: granular.paros,
          costos: digestCostos(base),
        },
        setProgresoPptx,
      );
      toast.success('Presentación generada. Revise su carpeta de descargas.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'No pudimos generar la presentación.');
    } finally {
      setGenerandoPptx(false);
      setProgresoPptx(null);
    }
  };

  return (
    <RoleLayout sidebar={<CoordinadorSidebar />} roleLabel="Coordinador">
      <div className="flex flex-col gap-4">
        <header className="flex flex-col gap-3 border-b border-border pb-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-xl font-medium text-fg">Informe gerencial</h1>
            <p className="mt-1 text-[11px] text-fg-tertiary">
              PDF con marca Jidokap · seleccione el período, la comparación y los indicadores ·
              producción validada por supervisión
            </p>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="primary"
                onClick={descargar}
                disabled={!puedeDescargar || generandoPptx}
              >
                {generando
                  ? 'Generando…'
                  : cargando
                    ? 'Cargando datos…'
                    : 'Descargar informe (PDF)'}
              </Button>
              <Button
                variant="outline"
                onClick={descargarPptx}
                disabled={generandoPptx || generando || cargando || !periodo}
                title="Presentación con la estructura del deck del cliente (por área, línea y mes)"
              >
                {generandoPptx ? 'Generando…' : 'Descargar (PowerPoint)'}
              </Button>
            </div>
            <label className="flex cursor-pointer items-center gap-1.5 text-[11px] text-fg-secondary">
              <input
                type="checkbox"
                checked={incluirRrhh}
                onChange={(e) => setIncluirRrhh(e.target.checked)}
                className="accent-[var(--accent)]"
              />
              Incluir espacios reservados (RRHH) en el PowerPoint
            </label>
            {progresoPptx && (
              <p className="text-[11px] tabular-nums text-fg-tertiary" aria-live="polite">
                {progresoPptx}
              </p>
            )}
          </div>
        </header>

        {/* Período (selector compartido) + comparación */}
        <Card>
          <CardContent className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label>Período del informe</Label>
              <BarraPeriodo ctrl={per} presets={PRESETS_INFORME} />
              {per.periodo.parcialLabel ? (
                <p className="text-[11px] text-fg-tertiary">
                  {per.periodo.parcialLabel} · el informe declara el corte en la portada
                </p>
              ) : null}
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <Label htmlFor="inf-comp">Comparar con</Label>
                <Select
                  id="inf-comp"
                  value={modoComp}
                  onChange={(e) => setModoComp(e.target.value as ModoComparacion)}
                >
                  <option value="ninguno">Ninguno</option>
                  <option value="anterior">
                    {per.periodo.isCurrent
                      ? 'Período anterior, a la misma altura'
                      : 'Período anterior completo'}
                  </option>
                  <option value="mes_anterior">Mes anterior completo</option>
                  <option value="personalizado">Personalizado</option>
                </Select>
              </div>
              {modoComp === 'personalizado' && (
                <>
                  <div>
                    <Label htmlFor="inf-comp-desde">Desde</Label>
                    <Input
                      id="inf-comp-desde"
                      type="date"
                      value={compCustom.desde}
                      onChange={(e) => setCompCustom((c) => ({ ...c, desde: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="inf-comp-hasta">Hasta</Label>
                    <Input
                      id="inf-comp-hasta"
                      type="date"
                      value={compCustom.hasta}
                      onChange={(e) => setCompCustom((c) => ({ ...c, hasta: e.target.value }))}
                    />
                  </div>
                </>
              )}
              {compLabel && (
                <p className="pb-2 text-[11px] tabular-nums text-fg-secondary">{compLabel}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Checklist + previews */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {SECCIONES_INFORME.map((s) => {
            const activa = seleccion.has(s.id);
            const preview = lista.find((p) => p.id === s.id);
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => toggle(s.id)}
                aria-pressed={activa}
                className={cn(
                  'flex flex-col gap-1 rounded-md border p-3 text-left transition-colors',
                  activa
                    ? 'border-fg/60 bg-surface-overlay/40'
                    : 'border-border opacity-60 hover:opacity-90',
                )}
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="text-[12px] font-medium text-fg">{s.titulo}</span>
                  <span
                    className={cn(
                      'inline-flex h-4 w-4 items-center justify-center rounded border text-[10px]',
                      activa ? 'border-fg bg-fg text-surface-base' : 'border-border',
                    )}
                    aria-hidden
                  >
                    {activa ? '✓' : ''}
                  </span>
                </span>
                <span className="text-[10.5px] text-fg-tertiary">{s.detalle}</span>
                {activa && (
                  <span className="mt-1 flex items-baseline justify-between">
                    <span className="text-lg font-semibold tabular-nums text-fg">
                      {preview?.estado === 'cargando' ? (
                        <Spinner className="size-4" />
                      ) : (
                        preview?.headline
                      )}
                    </span>
                    <span
                      className={cn(
                        'text-[10px] uppercase tracking-wider',
                        preview?.estado === 'sin_datos' ? 'text-warn' : 'text-fg-tertiary',
                      )}
                    >
                      {preview?.estado === 'lista'
                        ? 'Lista'
                        : preview?.estado === 'sin_datos'
                          ? 'Sin datos'
                          : 'Cargando'}
                    </span>
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <p className="text-[11px] text-fg-tertiary">
          Las secciones marcadas «Sin datos» se omiten del PDF (el resto se genera igual). Bases de
          los datos: producción, cumplimiento y facturación usan únicamente información validada por
          supervisión (las tiendas entran con su producción validada, sin programa derivado); paros,
          Entrada a Pits y los rankings de personal usan el registro operativo cerrado, que no está
          sujeto a validación de producción. El tiempo de paro del informe es el{' '}
          <span className="text-fg-secondary">paro total registrado</span> (incluye programados); la
          pérdida en $ solo cuenta lo que afecta disponibilidad; los paros abiertos al cierre no
          suman y se declaran aparte.
        </p>
      </div>
    </RoleLayout>
  );
}
